/**
 * KanbanCard — compact card representation shown inside a column.
 *
 * Design round 2 (2026-05-15): NO border, NO shadow. The card is just
 * a hover-able row with a soft bg in idle state and a slightly stronger
 * bg on hover. Notion-style: groupings come from spacing + typography,
 * not chrome. Drag-handle is hover-revealed.
 *
 * Click → open card detail in the right-side panel (non-modal, the
 * board behind stays interactive).
 */

import type { Card } from '@chaos/types/kanban';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { GripVertical } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

interface KanbanCardProps {
  card: Card;
  boardId: string;
  /** When true, the card is being dragged — caller dims/elevates it. */
  isOverlay?: boolean;
}

export const KanbanCard = React.memo(function KanbanCard({
  card,
  boardId,
  isOverlay,
}: KanbanCardProps) {
  const matchRoute = useMatchRoute();
  const isActive = !!matchRoute({
    to: '/board/$boardId/card/$cardId',
    params: { boardId, cardId: card.id },
  });

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: card.id,
    data: { type: 'card', card },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/card relative rounded border border-border/60 transition-colors',
        isActive ? 'bg-muted' : 'bg-background hover:bg-muted/60',
        isDragging && 'opacity-40',
        isOverlay && 'shadow-md bg-background border-foreground/20',
      )}
      data-card-id={card.id}
    >
      <button
        type="button"
        aria-label="Drag card"
        className={cn(
          'absolute left-0 top-1/2 -translate-y-1/2 flex h-5 w-4 cursor-grab touch-none items-center justify-center text-muted-foreground/50',
          'opacity-0 transition-opacity group-hover/card:opacity-100',
          'active:cursor-grabbing',
        )}
        {...attributes}
        {...listeners}
        onClick={(e) => e.preventDefault()}
      >
        <GripVertical className="h-3 w-3" aria-hidden />
      </button>
      <Link
        to="/board/$boardId/card/$cardId"
        params={{ boardId, cardId: card.id }}
        className="block px-3 py-1.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
      >
        <p className="line-clamp-2 text-[13px] leading-snug text-foreground">{card.title}</p>
        {card.autonomyLevel && (
          <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {card.autonomyLevel}
          </p>
        )}
      </Link>
    </div>
  );
});
